import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircleOutline as OrderIcon,
  CancelOutlined as CancelIcon,
  LockOutlined as CloseIcon,
  LocalShipping as ReceiveIcon,
  EditOutlined as EditIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  ArrowDropDown as DropdownIcon,
  AddOutlined as AddIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import purchaseOrderService from '../../../../services/purchaseOrderService';
import venueSupplierService from '../../../../services/venueSupplierService';
import POStatusBadge from '../../../../components/venue/POStatusBadge';
import { downloadPurchaseOrderPdf } from '../../../../utils/purchaseOrderPdf';
import { downloadPurchaseOrderExcel } from '../../../../utils/purchaseOrderExcel';
import { formatPoLineSize } from '../../../../utils/poLineSize';
import InvoiceDialog from '../../../../components/venue/InvoiceDialog';
import InvoiceDetailPanel from '../../../../components/venue/InvoiceDetailPanel';
import type { PurchaseOrder, PurchaseOrderLine, GoodsReceipt, Supplier, Invoice } from '../../../../types/venue';

const PurchaseOrderDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId, venueName, defaultCurrency } = useVenue();
  const { poId } = useParams<{ poId: string }>();
  const backPath = `/admin/venues/${venueId}/purchasing`;

  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [lines, setLines] = useState<PurchaseOrderLine[]>([]);
  const [receipts, setReceipts] = useState<GoodsReceipt[]>([]);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [supplierName, setSupplierName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);

  const load = useCallback(async () => {
    if (!poId) return;
    setLoading(true);
    setError(null);
    try {
      const [po, linesResult, receiptsResult] = await Promise.all([
        purchaseOrderService.getOrder(venueId, poId),
        purchaseOrderService.getLines(venueId, poId),
        purchaseOrderService.getReceipts(venueId, poId),
      ]);
      setOrder(po);
      setLines(linesResult.data ?? []);
      setReceipts(receiptsResult.data ?? []);

      // Load full supplier — needed for PDF and name display.
      // Venue-scoped lookup so VENUE_USER doesn't 403 on the global supplier
      // endpoint and so other venues' supplier records can't be probed via
      // a poisoned supplierId.
      if (venueId) {
        venueSupplierService.getVenueSupplier(venueId, po.supplierId)
          .then(s => { setSupplier(s); setSupplierName(s.name); })
          .catch(() => { /* non-blocking */ });
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  }, [poId, venueId]);

  useEffect(() => { load(); }, [load]);

  // ── Actions ───────────────────────────────────────────────────────────────────
  const runAction = async (label: string, fn: () => Promise<PurchaseOrder>) => {
    setActionError(null);
    setActionLoading(true);
    try {
      const updated = await fn();
      setOrder(updated);
    } catch (err: any) {
      setActionError(err?.response?.data?.message ?? err?.message ?? `Failed to ${label}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkOrdered = () => {
    if (!poId || !venueId || !window.confirm('Mark this order as placed? This locks the order lines.')) return;
    runAction('mark as ordered', () => purchaseOrderService.markOrdered(venueId, poId));
  };

  const handleCancel = () => {
    if (!poId || !venueId || !window.confirm('Cancel this purchase order? This cannot be undone if stock has been received.')) return;
    runAction('cancel', () => purchaseOrderService.cancel(venueId, poId));
  };

  const handleClose = () => {
    if (!poId || !venueId || !window.confirm('Close this purchase order? No further receiving will be possible.')) return;
    runAction('close', () => purchaseOrderService.close(venueId, poId));
  };

  const handleDownloadPdf = async () => {
    if (!order) return;
    setDownloadAnchor(null);
    setPdfError(null);
    setPdfLoading(true);
    try {
      await downloadPurchaseOrderPdf(order, lines, supplier, venueName, defaultCurrency);
    } catch (err: any) {
      setPdfError(err?.message ?? 'Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!order) return;
    setDownloadAnchor(null);
    downloadPurchaseOrderExcel(order._id, lines, supplierName);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const formatDate = (iso?: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // W11 — formatBottleSize replaced by the shared formatPoLineSize util,
  // which prefers the canonical sizeValue+sizeUnit snapshot and falls back
  // to legacy bottleSize for older lines.
  const formatLineSize = (line: PurchaseOrderLine): string =>
    formatPoLineSize(line) || '—';

  // ── Status-based action buttons ───────────────────────────────────────────────
  const renderActions = () => {
    if (!order) return null;
    const disabled = actionLoading;

    return (
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={e => setDownloadAnchor(e.currentTarget)}
          disabled={pdfLoading || lines.length === 0}
          endIcon={pdfLoading ? <CircularProgress size={16} color="inherit" /> : <DropdownIcon />}
          sx={{ textTransform: 'none' }}
        >
          {pdfLoading ? 'Generating…' : 'Download'}
        </Button>
        <Menu
          anchorEl={downloadAnchor}
          open={Boolean(downloadAnchor)}
          onClose={() => setDownloadAnchor(null)}
        >
          <MenuItem onClick={handleDownloadPdf} sx={{ gap: 1.5 }}>
            <PdfIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            Download PDF
          </MenuItem>
          <MenuItem onClick={handleDownloadExcel} sx={{ gap: 1.5 }}>
            <ExcelIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            Download Excel
          </MenuItem>
        </Menu>
        {order.status === 'draft' && (
          <>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`${backPath}/${poId}/edit`)}
              disabled={disabled}
              sx={{ textTransform: 'none' }}
            >
              Edit Draft
            </Button>
            <Button
              variant="contained"
              startIcon={<OrderIcon />}
              onClick={handleMarkOrdered}
              disabled={disabled || lines.length === 0}
              title={lines.length === 0 ? 'Add at least one line before ordering' : undefined}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Mark Ordered
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={disabled}
              sx={{ textTransform: 'none' }}
            >
              Cancel Order
            </Button>
          </>
        )}
        {order.status === 'ordered' && (
          <>
            <Button
              variant="contained"
              startIcon={<ReceiveIcon />}
              onClick={() => navigate(`${backPath}/${poId}/receive`)}
              disabled={disabled}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Receive Delivery
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleClose}
              disabled={disabled}
              sx={{ textTransform: 'none' }}
            >
              Close Order
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={disabled}
              sx={{ textTransform: 'none' }}
            >
              Cancel Order
            </Button>
          </>
        )}
        {order.status === 'partially_received' && (
          <>
            <Button
              variant="contained"
              startIcon={<ReceiveIcon />}
              onClick={() => navigate(`${backPath}/${poId}/receive`)}
              disabled={disabled}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Receive More
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleClose}
              disabled={disabled}
              sx={{ textTransform: 'none' }}
            >
              Close Order
            </Button>
          </>
        )}
        {order.status === 'received' && (
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={handleClose}
            disabled={disabled}
            sx={{ textTransform: 'none' }}
          >
            Close Order
          </Button>
        )}
        {actionLoading && <CircularProgress size={20} />}
      </Box>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}>
          Purchase Orders
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!order) return null;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Top nav row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(backPath)}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Purchase Orders
        </Button>
        {renderActions()}
      </Box>

      {actionError && <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>}
      {pdfError && <Alert severity="error" onClose={() => setPdfError(null)}>{pdfError}</Alert>}

      {/* PO Header */}
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Order ID
            </Typography>
            <Typography variant="body1" fontWeight={700} fontFamily="monospace" fontSize="0.9rem" mt={0.25}>
              {order._id.slice(-8).toUpperCase()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Supplier
            </Typography>
            <Typography variant="body1" mt={0.25}>{supplierName || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Status
            </Typography>
            <Box mt={0.5}><POStatusBadge status={order.status} size="medium" /></Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Ordered
            </Typography>
            <Typography variant="body1" mt={0.25}>{formatDate(order.orderedAt)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Expected Delivery
            </Typography>
            <Typography variant="body1" mt={0.25}>{formatDate(order.expectedDeliveryDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Created
            </Typography>
            <Typography variant="body1" mt={0.25}>{formatDate(order.createdAt)}</Typography>
          </Box>
        </Box>
        {order.notes && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">{order.notes}</Typography>
          </>
        )}
        <Typography variant="caption" fontFamily="monospace" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
          ID: {order._id}
        </Typography>
      </Paper>

      {/* Order Lines */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={600}>Order Lines</Typography>
          <Typography variant="caption" color="text.secondary">{lines.length} {lines.length === 1 ? 'line' : 'lines'}</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Item', 'Size', 'Qty Ordered', 'Unit Price'].map(h => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <Typography variant="body2">No lines on this order.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                lines.map(line => (
                  <TableRow key={line._id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{line.displayName}</Typography>
                      {line.supplierItemCode && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {line.supplierItemCode}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{formatLineSize(line)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{line.orderedQty}</Typography>
                    </TableCell>
                    <TableCell>
                      {/* W11 — DRAFT lines without a stored unitPrice show the
                          backend-hydrated suggestedUnitPrice (sourced from the
                          supplier link's lastReceivedCost) in muted italic so
                          it's clearly a suggestion, not a committed price.
                          Posted lines always show the stored value or em-dash. */}
                      {line.unitPrice != null ? (
                        <Typography variant="body2" color="text.secondary">
                          {`${defaultCurrency}${line.unitPrice.toFixed(2)}`}
                        </Typography>
                      ) : line.suggestedUnitPrice != null ? (
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                          title="Suggested from supplier cost. Save the line to commit."
                        >
                          {`${defaultCurrency}${line.suggestedUnitPrice.toFixed(2)}`}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Goods Receipts */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={600}>Goods Receipts</Typography>
          <Typography variant="caption" color="text.secondary">{receipts.length} {receipts.length === 1 ? 'receipt' : 'receipts'}</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Receipt ID', 'Received Date', 'Received By', 'Status'].map(h => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {receipts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <Typography variant="body2">No receipts yet.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                receipts.map(receipt => (
                  <TableRow key={receipt._id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.78rem" fontWeight={600}>
                        {receipt._id.slice(-8).toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(receipt.receivedDate)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{receipt.receivedBy}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: receipt.postedAt ? '#1a7f37' : '#9a6700',
                          backgroundColor: receipt.postedAt ? '#dafbe1' : '#fef9c3',
                          px: 1, py: 0.25, borderRadius: 1,
                        }}
                      >
                        {receipt.postedAt ? 'Posted' : 'Draft'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Invoices */}
      {(() => {
        const invoices: Invoice[] = order.invoices ?? [];
        const invoiceStatusStyle = (status: Invoice['status']) => {
          if (status === 'open')  return { color: '#1a7f37', backgroundColor: '#dafbe1' };
          if (status === 'void')  return { color: '#57606a', backgroundColor: '#f6f8fa' };
          return                         { color: '#9a6700', backgroundColor: '#fef9c3' }; // draft
        };
        return (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" fontWeight={600}>Invoices</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => { setEditingInvoice(null); setInvoiceDialogOpen(true); }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Add Invoice
                </Button>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Invoice Number', 'Status', 'Invoice Date', 'Total Amount'].map(h => (
                      <TableCell
                        key={h}
                        sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
                          <Typography variant="body2">No invoices yet.</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.map(inv => (
                      <TableRow
                        key={inv._id}
                        hover
                        onClick={() => setViewingInvoice(inv)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{inv.invoiceNumber}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, px: 1, py: 0.25, borderRadius: 1, ...invoiceStatusStyle(inv.status) }}
                          >
                            {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{formatDate(inv.invoiceDate)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {inv.totalAmount != null ? `${defaultCurrency}${inv.totalAmount.toFixed(2)}` : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })()}

      <InvoiceDialog
        open={invoiceDialogOpen}
        onClose={() => { setInvoiceDialogOpen(false); setEditingInvoice(null); }}
        onSuccess={load}
        venueId={venueId}
        purchaseOrderId={poId!}
        supplierId={order.supplierId}
        invoice={editingInvoice ?? undefined}
      />

      {viewingInvoice && (
        <InvoiceDetailPanel
          open
          onClose={() => setViewingInvoice(null)}
          onSuccess={() => { load(); setViewingInvoice(null); }}
          invoice={viewingInvoice}
          venueId={venueId}
          supplierName={supplierName}
        />
      )}
    </Box>
  );
};

export default PurchaseOrderDetail;
